import { useCallback, useEffect, useState } from "react";

// Safety net if `afterprint` never fires (some mobile share-sheet flows).
const AFTERPRINT_FALLBACK_MS = 60000;

export interface ReportPrint {
  /** Mount the print copy, then open the browser's print dialog. */
  print: () => void;
  /** True while the print copy should be mounted. */
  printing: boolean;
}

/**
 * Print on demand (useGiftCardPrint pattern): the print-only copy of a report
 * is mounted only while printing — a large tree isn't rendered twice the rest
 * of the time — and removed again on `afterprint`. `canPrint` is false while
 * there's no settled result to print: if a re-run starts between the click
 * and the frame, printing is called off instead of opening an empty page.
 */
export function useReportPrint(canPrint: boolean): ReportPrint {
  const [printing, setPrinting] = useState(false);

  useEffect(() => {
    if (!printing) return undefined;
    if (!canPrint) {
      setPrinting(false);
      return undefined;
    }
    const done = () => setPrinting(false);
    window.addEventListener("afterprint", done);
    // Wait a frame so the print copy is committed and laid out.
    const raf = requestAnimationFrame(() => window.print());
    const fallback = setTimeout(done, AFTERPRINT_FALLBACK_MS);
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(fallback);
      window.removeEventListener("afterprint", done);
    };
  }, [printing, canPrint]);

  const print = useCallback(() => setPrinting(true), []);
  return { print, printing };
}
